import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const NOTICE_TYPES = ["CP2000", "CP14", "CP504", "LT11", "Audit", "Other"];

export default function Notices() {
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || user?.role === "cpa";
  const canEditClient = ["admin", "cpa", "document_team"].includes(user?.role);
  const [notices, setNotices] = useState([]);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [form, setForm] = useState({ notice_type: "CP2000", irs_reference: "", amount: 0, due_date: "", status: "open", description: "" });

  const load = useCallback(async () => {
    const { data } = await api.get("/notices");
    setNotices(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post("/notices", { ...form, amount: Number(form.amount) });
      toast.success("Notice logged");
      setOpen(false);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/notices/${toDelete.id}`);
      toast.success("Notice deleted");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="notices-title">IRS Notices</h1>
          <p className="text-slate-600 mt-1">Every notice with a clear owner and due date.</p>
        </div>
        {canEditClient && (
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="new-notice-btn"><Plus size={16} className="mr-1" /> Log notice</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Log an IRS notice</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Notice type</Label>
                  <Select value={form.notice_type} onValueChange={(v) => setForm({ ...form, notice_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{NOTICE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>IRS reference</Label>
                  <Input value={form.irs_reference} onChange={(e) => setForm({ ...form, irs_reference: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount ($)</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                </div>
                <div>
                  <Label>Due date</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-[#0A2540]" onClick={submit} data-testid="notice-submit">Log notice</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <div className="rounded-md border tf-hairline bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="notices-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Reference</th>
              <th className="text-left p-3">Owner</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-left p-3">Due</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Description</th>
              {canDelete && <th />}
            </tr>
          </thead>
          <tbody>
            {notices.length === 0 && <tr><td colSpan={canDelete ? 8 : 7} className="p-6 text-center text-slate-500">No notices logged.</td></tr>}
            {notices.map(n => (
              <tr key={n.id} className="border-t tf-hairline hover:bg-slate-50">
                <td className="p-3 font-mono">{n.notice_type}</td>
                <td className="p-3 font-mono text-xs">{n.irs_reference}</td>
                <td className="p-3">{n.owner_name}</td>
                <td className="p-3 text-right font-mono">${Number(n.amount || 0).toLocaleString()}</td>
                <td className="p-3">{n.due_date}</td>
                <td className="p-3"><span className={`text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded ${n.status === 'open' ? 'bg-[#D32F2F]/10 text-[#D32F2F]' : 'bg-emerald-50 text-emerald-700'}`}>{n.status}</span></td>
                <td className="p-3 text-slate-600 max-w-xs truncate">{n.description}</td>
                {canDelete && (
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setToDelete(n)} data-testid={`delete-notice-${n.id}`}>
                      <Trash size={14} className="text-[#D32F2F]" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete IRS notice?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && `${toDelete.notice_type} · ${toDelete.irs_reference}`}. This permanently removes the notice from the register. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-notice-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="delete-notice-confirm">
              Delete notice
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
